import { ReactNode, useEffect, useState } from 'react';

/**
 * DelayedRender prevents layout thrashing by deferring the rendering of
 * loading spinners and other transient UI elements until a minimum delay
 * has passed.
 *
 * This is particularly useful for fast-completing operations where showing
 * a loading spinner for just a few frames creates visual noise and a poor
 * user experience. By delaying the appearance of loading states, the UI
 * remains stable when operations complete quickly.
 *
 * @example
 * ```tsx
 * // Only show spinner if loading takes more than 2 seconds
 * if (isLoading) {
 *   return (
 *     <DelayedRender delayMs={2000}>
 *       <Spinner />
 *     </DelayedRender>
 *   );
 * }
 * ```
 *
 * @param children - The content to render after the delay
 * @param delayMs - Delay in milliseconds (default: 1000)
 */
export function DelayedRender({ children, delayMs }: { children: ReactNode; delayMs?: number }) {
  const timeout = delayMs ?? 1000;
  const [show, setShow] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShow(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout]);

  if (!show) {
    return null;
  }

  return children;
}
